import React, { ChangeEvent, useEffect, useState } from 'react';
import { Grid, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import { UserContactModel, ContactTypeSelectItems, ContactType } from '../../interfaces';
import { UserContactSchema } from '../../validationSchemes';
import { EntityEditDialog, SelectInput, TextInput, DeleteIcon, PhoneNumberInput } from '@lp/lp-ui';
import {
    createUserContact, updateUserContact, deleteUserContact, UserContactRequest
} from '../../app/currentUserSlice'
import { useAppDispatch } from '../../app/hooks';
import { NewUserContactModel } from '../../functions';
import { ApiViewResponseError } from '../../interfaces';

type ContactDialogProps = {
    open: boolean,
    data: UserContactModel,
    handleUnauthorized: () => void,
    handleClose: () => void
}

const ContactDialog: React.FC<ContactDialogProps> = ({ open, data, handleUnauthorized, handleClose }: ContactDialogProps) => {
    const dispatch = useAppDispatch();
    const isNew = data.id === 0;
    const [contactTypeSelected, setContactTypeSelected] = useState<ContactType>(data.contactType);

    useEffect(() => {
        setContactTypeSelected(data.contactType);
    }, [data]);

    const formik = useFormik<UserContactModel>({
        initialValues: data
            ? { ...data, phoneExtension: data.phoneExtension !== undefined ? data.phoneExtension : "" }
            : NewUserContactModel(),
        enableReinitialize: true,
        validationSchema: UserContactSchema,
        onSubmit: async (values) => {
            if (isNew) {
                const result = await dispatch(createUserContact(values));
                if (result.meta.requestStatus === "fulfilled") {
                    handlePopupClose();
                }
                else {
                    const errors = result.payload as ApiViewResponseError[];
                    if (errors.some(e => e.status === '401')) {
                        handleUnauthorized();
                    }
                }
            }
            else {
                const request: UserContactRequest = {
                    contactId: data.id,
                    contact: values
                };
                const result = await dispatch(updateUserContact(request));
                if (result.meta.requestStatus === "fulfilled") {
                    handlePopupClose();
                }
                else {
                    const errors = result.payload as ApiViewResponseError[];
                    if (errors.some(e => e.status === '401')) {
                        handleUnauthorized();
                    }
                }
            }
        }
    });

    const {
        errors,
        touched,
        values,
        getFieldProps,
        submitForm,
        handleChange
    } = formik;

    const handlePopupClose = () => {
        formik.resetForm();
        handleClose();
    }

    const handleDelete = () => {
        dispatch(deleteUserContact(data.id));
        handlePopupClose();
    }

    const onContactTypeChanged = (e: React.ChangeEvent<any>) => {
        handleChange(e);
        setContactTypeSelected(e.target.value);
    }

    return (
        <React.Fragment>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={submitForm}>
                    <EntityEditDialog
                        open={open}
                        editing={true}
                        maxWidth="sm"
                        fullWidth
                        title="Contact"
                        handleClose={handlePopupClose}
                        handleSave={submitForm}
                        showMoreMenu={!isNew}
                        menuItems={[
                            <MenuItem key="menu-item-i" onClick={handleDelete}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    Delete
                                </ListItemText>
                            </MenuItem>
                        ]}
                    >
                        <Grid container spacing={0} columnSpacing={6}>
                            <Grid item xs={12}>
                                <SelectInput
                                    label="Type"
                                    isEditing={true}
                                    required
                                    {...getFieldProps("contactType")}
                                    options={ContactTypeSelectItems}
                                    onChange={onContactTypeChanged}
                                    error={Boolean(touched.contactType && errors.contactType)}
                                    helperText={touched.contactType && errors.contactType}
                                    value={ContactTypeSelectItems.find(ctt => ctt.value === values.contactType)?.value}
                                />
                            </Grid>
                            <Grid item xs={contactTypeSelected === ContactType.Phone ? 9 : 12}>
                                {contactTypeSelected === ContactType.Email &&
                                    <TextInput
                                        isEditing={true}
                                        required
                                        type="string"
                                        label="Contact"
                                        {...getFieldProps("contact")}
                                        error={Boolean(touched.contact && errors.contact)}
                                        helperText={touched.contact && errors.contact}
                                    />
                                }
                                {contactTypeSelected !== ContactType.Email &&
                                    <PhoneNumberInput
                                        isEditing={true}
                                        defaultCountry="US"
                                        label="Contact"
                                        {...getFieldProps("contact")}
                                        onChange={(e: string | ChangeEvent<any>) => { formik.setFieldValue("contact", e); } }
                                        error={Boolean(touched.contact && errors.contact)}
                                        helperText={touched.contact && errors.contact}
                                    />
                                }
                            </Grid>
                            {contactTypeSelected === ContactType.Phone &&
                                <Grid item xs={3}>
                                    <TextInput
                                        isEditing={true}
                                        type="string"
                                        label="Extension"
                                        inputProps={{ maxLength: 3 }}
                                        {...getFieldProps("phoneExtension")}
                                        error={Boolean(touched.phoneExtension && errors.phoneExtension)}
                                        helperText={touched.phoneExtension && errors.phoneExtension}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </EntityEditDialog>
                </Form>
            </FormikProvider>
        </React.Fragment>
    );
}

export default ContactDialog;
